import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Notification,
  BookSquare,
  Game,
  Star1,
  Folder,
  MessageQuestion,
  Magicpen,
  Heart,
  PlayCircle,
  Book,
  SearchNormal1,
  Category,
  DocumentText,
  Location,
} from "iconsax-react";

import { useAuth } from "../../hooks/auth";
import { useProfiles } from "../../hooks/profile";
import { useWordOfTheDay } from "../../hooks/useWordOfTheDay";
import { useGamification } from "../../hooks/useGamification";
import MobileLayout from "../../components/MobileLayout";
import {
  WordOfDayCard,
  WodBadgeRow,
  WodBadge,
  PlaybookIconBox,
  WodTitle,
  WodDesc,
  WodFooter,
  WodButton,
  HeartBtn,
  SectionTitle,
  TitleBadge,
  HorizontalScroll,
  FeatureCard,
  FeatureIconBox,
  FeatureName,
  ListSection,
  ListHeader,
  ListSubtext,
  ListItemsGrid,
  ListItem,
  ListItemIconBox,
  ListItemContent,
  ListItemTitle,
  ListItemSubtitle,
  FooterSection,
  AboutLink,
  SeloBox,
  SupportText,
} from "./styles";

const Dashboard: React.FC = () => {
  const { getAll } = useProfiles();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addPoints } = useGamification();
  const {
    currentWord,
    loading: wodLoading,
    isFavorite,
    toggleFavorite,
  } = useWordOfTheDay();

  useEffect(() => {
    if (user?.uid) {
      addPoints("DAILY_LOGIN");
    }
  }, [user?.uid, addPoints]);

  useEffect(() => {
    async function loadData() {
      if (!user?.uid) return;
      try {
        await getAll(user.uid);
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
      }
    }
    loadData();
  }, [getAll, user?.uid]);

  const recentFeatures = [
    {
      id: "dictionary",
      name: "Dicionário INES",
      type: "Alfabeto",
      progress: "80%",
      bg: "#E0F2FE",
      color: "#4614BB",
      icon: <Book size={20} variant="Bold" />,
      path: "/sinalario",
    },
    {
      id: "map",
      name: "Mapa de Libras",
      type: "Locais",
      progress: "",
      bg: "#DBEAFE",
      color: "#3B82F6",
      icon: <Location size={20} variant="Bold" />,
      path: "/map",
    },
    {
      id: "lessons",
      name: "Lições",
      type: "Estudo",
      progress: "",
      bg: "#FCE7F3",
      color: "#EC4899",
      icon: <BookSquare size={20} variant="Bold" />,
      path: "/lessons",
    },
    {
      id: "favorites",
      name: "Meus favoritos",
      type: "Salvos",
      progress: "20%",
      bg: "#FCE7F3",
      color: "#EC4899",
      icon: <Heart size={20} variant="Bold" />,
      path: "/favorites",
    },
    {
      id: "games",
      name: "Jogos Educativos",
      type: "Prática",
      progress: "60%",
      bg: "#DCFCE7",
      color: "#10B981",
      icon: <Game size={20} variant="Bold" />,
      path: "/games",
    },
  ];

  const goToVerbete = (opts?: { autoPlayVideo?: boolean }) => {
    if (!currentWord) return;
    navigate("/VerbeteDetails", {
      state: { ...currentWord, autoPlayVideo: !!opts?.autoPlayVideo },
    });
  };

  const handleWodFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user?.uid) {
      await Swal.fire({
        icon: "info",
        title: "Login necessário",
        text: "Entre na sua conta para favoritar a palavra do dia.",
      });
      navigate("/login");
      return;
    }
    await toggleFavorite();
  };

  return (
    <MobileLayout headerTitle="Dashboard" headerType="dashboard">
      {wodLoading && (
        <WordOfDayCard>
          <WodBadgeRow>
            <WodBadge>PALAVRA DO DIA</WodBadge>
            <PlaybookIconBox>
              <Book size={32} color="white" variant="Linear" />
            </PlaybookIconBox>
          </WodBadgeRow>
          <WodDesc>Carregando palavra do dia…</WodDesc>
        </WordOfDayCard>
      )}
      {!wodLoading && currentWord && (
        <WordOfDayCard
          role="button"
          tabIndex={0}
          onClick={() => goToVerbete()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              goToVerbete();
            }
          }}
        >
          <WodBadgeRow>
            <WodBadge>PALAVRA DO DIA</WodBadge>
            <PlaybookIconBox>
              <Book size={32} color="white" variant="Linear" />
            </PlaybookIconBox>
          </WodBadgeRow>
          <WodTitle>{currentWord.palavra}</WodTitle>
          <WodDesc>
            {currentWord.descricao?.trim() ||
              "Toque para ver o sinal e o significado completo."}
          </WodDesc>
          <WodFooter>
            <WodButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goToVerbete({ autoPlayVideo: true });
              }}
            >
              <PlayCircle size={20} variant="Bold" />
              Ver vídeo
            </WodButton>
            <HeartBtn
              type="button"
              $isFavorite={isFavorite}
              onClick={handleWodFavorite}
              aria-label={isFavorite ? "Remover dos favoritos" : "Favoritar"}
            >
              <Heart size={24} variant={isFavorite ? "Bold" : "Linear"} />
            </HeartBtn>
          </WodFooter>
        </WordOfDayCard>
      )}

      {/* Último Acesso (Recent Features) */}
      <SectionTitle>
        Último acesso
        <TitleBadge>{recentFeatures.length}</TitleBadge>
      </SectionTitle>

      <HorizontalScroll>
        {recentFeatures.map((feature) => (
          <FeatureCard key={feature.id} onClick={() => navigate(feature.path)}>
            <FeatureIconBox $color={feature.color}>
              {feature.icon}
            </FeatureIconBox>
            <FeatureName>{feature.name}</FeatureName>
          </FeatureCard>
        ))}
      </HorizontalScroll>

      {/* Comece por aqui */}
      <ListSection>
        <ListHeader>Comece por aqui</ListHeader>
        <ListSubtext>
          Escolha um objetivo para continuar aprendendo Libras.
        </ListSubtext>

        <ListItemsGrid>
          <ListItem onClick={() => navigate("/sinalario")}>
            <ListItemIconBox $color="#4614BB">
              <BookSquare size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Sinalário INES</ListItemTitle>
              <ListItemSubtitle>Dicionário completo</ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/map")}>
            <ListItemIconBox $color="#3B82F6">
              <Location size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Mapa de Libras</ListItemTitle>
              <ListItemSubtitle>Locais e pontos de interesse</ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/lessons")}>
            <ListItemIconBox $color="#EC4899">
              <BookSquare size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Lições</ListItemTitle>
              <ListItemSubtitle>Trilha de aprendizado</ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/games")}>
            <ListItemIconBox $color="#F59E0B">
              <Game size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Jogos Educativos</ListItemTitle>
              <ListItemSubtitle>Aprenda brincando</ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/ranking")}>
            <ListItemIconBox $color="#10B981">
              <Star1 size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Conquistas</ListItemTitle>
              <ListItemSubtitle>
                Veja suas conquistas e compare com outros
              </ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/sinalario")}>
            <ListItemIconBox $color="#06B6D4">
              <Magicpen size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Datilologia</ListItemTitle>
              <ListItemSubtitle>
                Abra uma palavra no sinalário e use “Ver datilologia”
              </ListItemSubtitle>
            </ListItemContent>
          </ListItem>
        </ListItemsGrid>
      </ListSection>

      {/* Outras Oções */}
      <ListSection>
        <ListHeader>Outras opções</ListHeader>

        <ListItemsGrid>
          <ListItem onClick={() => navigate("/repository")}>
            <ListItemIconBox $color="#5F33E1">
              <Folder size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Repositório pessoal</ListItemTitle>
              <ListItemSubtitle>Favoritos e vídeos de prática</ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/favorites")}>
            <ListItemIconBox $color="#EC4899">
              <Heart size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Favoritos</ListItemTitle>
              <ListItemSubtitle>Sinais que você curtiu</ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/search")}>
            <ListItemIconBox $color="#6E6A7C">
              <SearchNormal1 size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Buscar</ListItemTitle>
              <ListItemSubtitle>
                Encontre palavras no sinalário
              </ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/ai-assistant")}>
            <ListItemIconBox $color="#6D3DD8">
              <Magicpen size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Assistente IA</ListItemTitle>
              <ListItemSubtitle>Tutor e Tradutor</ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/sinalario/categories")}>
            <ListItemIconBox $color="#EC4899">
              <Category size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Categorias</ListItemTitle>
              <ListItemSubtitle>Explore sinais por tema</ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/sugestoes")}>
            <ListItemIconBox $color="#8B5CF6">
              <MessageQuestion size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Sugestões</ListItemTitle>
              <ListItemSubtitle>Enviar ou ver suas ideias</ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/ajuda")}>
            <ListItemIconBox $color="#F59E0B">
              <DocumentText size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Perguntas frequentes</ListItemTitle>
              <ListItemSubtitle>Dúvidas comuns sobre o app</ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/avisos")}>
            <ListItemIconBox $color="#10B981">
              <Notification size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Avisos</ListItemTitle>
              <ListItemSubtitle>Notícias e comunicados</ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/apoiar")}>
            <ListItemIconBox $color="#EF4444">
              <Heart size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Apoiar o Sinalário</ListItemTitle>
              <ListItemSubtitle>
                Ajude o app a continuar funcionando
              </ListItemSubtitle>
            </ListItemContent>
          </ListItem>

          <ListItem onClick={() => navigate("/comunidade")}>
            <ListItemIconBox $color="#5F33E1">
              <MessageQuestion size={24} variant="Bold" />
            </ListItemIconBox>
            <ListItemContent>
              <ListItemTitle>Comunidade</ListItemTitle>
              <ListItemSubtitle>Interaja com outros alunos</ListItemSubtitle>
            </ListItemContent>
          </ListItem>
        </ListItemsGrid>

        {(user?.type === "admin" || user?.isAdmin) && (
          <>
            <ListHeader style={{ marginTop: "24px" }}>
              Painel administrativo
            </ListHeader>

            <ListItemsGrid>
              <ListItem onClick={() => navigate("/manager")}>
                <ListItemIconBox $color="#4614BB">
                  <BookSquare size={24} variant="Bold" />
                </ListItemIconBox>
                <ListItemContent>
                  <ListItemTitle>Gerenciamento</ListItemTitle>
                  <ListItemSubtitle>
                    Módulos, turmas e conteúdos
                  </ListItemSubtitle>
                </ListItemContent>
              </ListItem>

              <ListItem onClick={() => navigate("/suggestions")}>
                <ListItemIconBox $color="#8B5CF6">
                  <MessageQuestion size={24} variant="Bold" />
                </ListItemIconBox>
                <ListItemContent>
                  <ListItemTitle>Sugestões recebidas</ListItemTitle>
                  <ListItemSubtitle>
                    Analisar envios dos usuários
                  </ListItemSubtitle>
                </ListItemContent>
              </ListItem>

              <ListItem onClick={() => navigate("/news")}>
                <ListItemIconBox $color="#10B981">
                  <Notification size={24} variant="Bold" />
                </ListItemIconBox>
                <ListItemContent>
                  <ListItemTitle>Notícias</ListItemTitle>
                  <ListItemSubtitle>Criar e editar avisos</ListItemSubtitle>
                </ListItemContent>
              </ListItem>

              <ListItem onClick={() => navigate("/faq")}>
                <ListItemIconBox $color="#F59E0B">
                  <MessageQuestion size={24} variant="Bold" />
                </ListItemIconBox>
                <ListItemContent>
                  <ListItemTitle>FAQ (gestão)</ListItemTitle>
                  <ListItemSubtitle>
                    Gerenciar perguntas frequentes
                  </ListItemSubtitle>
                </ListItemContent>
              </ListItem>
            </ListItemsGrid>
          </>
        )}
      </ListSection>

      <FooterSection>
        <AboutLink onClick={() => navigate("/about")}>Sobre o app</AboutLink>
        <SeloBox>
          {/* Usando uma URL placeholder para o selo, já que não encontramos localmente */}
          <img
            src="https://inovativa.online/wp-content/uploads/2023/04/selo-inovativa-de-impacto.png"
            alt="Selo Inovativa de Impacto"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <span>Este aplicativo foi acelerado pelo Inovativa de Impacto</span>
          <SupportText>Bilingua HUB — Web</SupportText>
        </SeloBox>
      </FooterSection>
    </MobileLayout>
  );
};

export default Dashboard;
